import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, Star, ArrowRight } from "lucide-react";
import { KineticLines } from "./Reveal";
import { WhatsAppIcon } from "./FloatingButtons";
import { IMAGES, RATINGS, waLink, telLink, BRAND } from "@/data/content";

const EASE = [0.76, 0, 0.24, 1];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.08, 1.25]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.62, 0.9]);

  return (
    <section
      id="inicio"
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-[100svh] items-end overflow-hidden bg-[#111111]"
    >
      {/* Parallax background */}
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img
          src={IMAGES.heroNight}
          alt="Guincho plataforma K2 Guincho 24h em São Paulo"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 z-[1] bg-black" />
      <div className="absolute inset-0 z-[1] bg-gradient-to-t from-[#111111] via-[#111111]/20 to-transparent" />

      {/* Content */}
      <div className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-16 pt-32 sm:px-8 sm:pb-24">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="mb-6 flex items-center gap-3"
        >
          <span className="h-px w-10 bg-[#D50000]" />
          <span className="font-mono-t text-xs uppercase tracking-[0.25em] text-[#FF4444]">
            Reboque & Socorro Automotivo · Zona Leste SP
          </span>
        </motion.div>

        <h1 className="font-display text-5xl font-bold uppercase leading-[0.92] tracking-tighter text-white sm:text-7xl lg:text-8xl">
          <KineticLines
            lines={["Guincho 24 Horas", "em São Paulo"]}
            start={0.15}
          />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6, ease: EASE }}
          className="mt-6 max-w-xl text-base text-zinc-300 sm:text-lg"
        >
          Atendimento rápido para automóveis e motocicletas. Chegamos rapidamente
          até você com segurança e preço justo.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.75, ease: EASE }}
          className="mt-8 flex flex-col gap-3 sm:flex-row"
        >
          <a
            data-testid="hero-whatsapp-btn"
            href={waLink()}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center justify-center gap-3 bg-[#25D366] px-8 py-4 text-sm font-bold uppercase tracking-wider text-black transition-colors duration-200 hover:bg-[#1EBE57]"
          >
            <WhatsAppIcon size={20} />
            Solicitar Guincho no WhatsApp
            <ArrowRight size={18} className="transition-transform duration-200 group-hover:translate-x-1" />
          </a>
          <a
            data-testid="hero-call-btn"
            href={telLink}
            className="group flex items-center justify-center gap-3 border border-white/25 bg-white/5 px-8 py-4 text-sm font-bold uppercase tracking-wider text-white backdrop-blur transition-colors duration-200 hover:border-[#D50000] hover:bg-[#D50000]"
          >
            <Phone size={18} />
            Ligar Agora
          </a>
        </motion.div>

        {/* Phone number written out */}
        <motion.a
          data-testid="hero-phone-text"
          href={telLink}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
          className="mt-6 inline-flex items-center gap-3 text-white transition-colors duration-200 hover:text-[#FF4444]"
        >
          <Phone size={20} className="text-[#FF4444]" />
          <span className="font-mono-t text-xs uppercase tracking-[0.2em] text-zinc-400">
            Ligue agora
          </span>
          <span className="font-display text-2xl font-bold tracking-tight sm:text-3xl">
            {BRAND.phoneDisplay}
          </span>
        </motion.a>

        {/* Rating badges */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.95, ease: EASE }}
          className="mt-12 grid grid-cols-2 gap-x-6 gap-y-4 border-t border-white/10 pt-8 sm:grid-cols-4"
        >
          {RATINGS.map((r) => (
            <div key={r} data-testid={`hero-rating-${r}`} className="flex flex-col gap-1">
              <div className="flex gap-0.5 text-[#D50000]">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={13} fill="currentColor" />
                ))}
              </div>
              <span className="text-xs font-semibold uppercase tracking-wide text-zinc-200 sm:text-sm">
                {r}
              </span>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
